import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { personsApi, type PersonsListParams, type ReferencesReport } from '../api/persons';
import type { Page, Person, PersonInput, UUID, WriteResult } from '../api/types';

export const personsKeys = {
  all:    ['persons'] as const,
  list:   (p: PersonsListParams) => ['persons', 'list', p] as const,
  // Task #245 (2026-06-02): includeDeleted in cache key so deleted-state
  // and alive-state fetches don't cross-contaminate after delete/restore.
  detail: (id: UUID, includeDeleted = false) =>
    ['persons', 'detail', id, includeDeleted ? 'with-deleted' : 'alive'] as const,
};

export function usePersons(
  params: PersonsListParams,
  opts?: { enabled?: boolean },
) {
  return useQuery<Page<Person>, ApiError>({
    queryKey: personsKeys.list(params),
    queryFn: () => personsApi.list(params),
    // #39 (2026-06-11): zero pills → no fetch; page renders the hint.
    enabled: opts?.enabled ?? true,
    // #46 (2026-06-12): keep previous rows visible while a filter-driven
    // refetch is in flight (no loading flash on each keystroke).
    placeholderData: keepPreviousData,
  });
}

export function usePerson(id: UUID | undefined, includeDeleted = false) {
  return useQuery<Person, ApiError>({
    queryKey: personsKeys.detail(id ?? '', includeDeleted),
    queryFn: () => personsApi.get(id!, includeDeleted),
    enabled: !!id,
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Person>, ApiError, PersonInput>({
    mutationFn: personsApi.create,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: personsKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      if (!r.pending) qc.setQueryData(personsKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useUpdatePerson(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<Person>, ApiError, PersonInput>({
    mutationFn: (payload) => personsApi.update(id, payload),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: personsKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      if (!r.pending) qc.setQueryData(personsKeys.detail(id), r.entity);
    },
  });
}

export function useDeletePerson() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Person>, ApiError, UUID>({
    mutationFn: personsApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: personsKeys.all }),
  });
}

export function useRestorePerson() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Person>, ApiError, UUID>({
    mutationFn: personsApi.restore,
    onSuccess: () => qc.invalidateQueries({ queryKey: personsKeys.all }),
  });
}

// Beta #11 cascade extension (2026-05-26).
export function usePersonReferences(id: UUID | undefined, enabled = true) {
  return useQuery<ReferencesReport, ApiError>({
    queryKey: ['persons', 'references', id ?? ''],
    queryFn: () => personsApi.references(id!),
    enabled: enabled && !!id,
  });
}

interface CascadeDeleteVars {
  id: UUID;
  refIds: Record<string, UUID[]>;
}

export function useCascadeDeletePerson() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Person>, ApiError, CascadeDeleteVars>({
    mutationFn: ({ id, refIds }) => personsApi.cascadeDelete(id, refIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: personsKeys.all });
      qc.invalidateQueries({ queryKey: ['agreement-parties'] });
      qc.invalidateQueries({ queryKey: ['account-parties'] });
      qc.invalidateQueries({ queryKey: ['insurance-policy-parties'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}
